import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Shield, Eye } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

function fmtDT(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function AdminConversationDetail() {
    const { conv_type, conv_id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Guard
    useEffect(() => {
        if (user && !user.is_super_admin) {
            toast.error("Accesso riservato ai super admin");
            navigate("/app");
        }
    }, [user, navigate]);

    // Fetch (registra audit automaticamente)
    useEffect(() => {
        (async () => {
            try {
                const { data: d } = await api.get(`/admin/conversations/${conv_type}/${conv_id}`, {
                    params: { access_reason: "routine_audit" },
                });
                setData(d);
            } catch (err) {
                if (err?.response?.status === 404) toast.error("Conversazione non trovata");
                else if (err?.response?.status === 403) { toast.error("Accesso riservato ai super admin"); navigate("/app"); }
                else toast.error("Errore caricamento conversazione");
            } finally { setLoading(false); }
        })();
    }, [conv_type, conv_id, navigate]);

    if (user && !user.is_super_admin) return null;

    return (
        <div style={{ padding: "24px 40px", maxWidth: 960 }} data-testid="admin-conv-detail-page">
            {/* Banner audit */}
            <div
                data-testid="admin-conv-audit-banner"
                style={{
                    background: "#181B20",
                    color: "#E5FF00",
                    padding: "12px 18px",
                    borderRadius: 2,
                    marginBottom: 24,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                }}
            >
                <Eye size={14} strokeWidth={2} />
                <span>Modalità lettura super admin · accesso registrato in audit</span>
            </div>

            <Link
                to="/app/admin/conversazioni"
                className="lt-link"
                data-testid="admin-conv-detail-back"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#5F6560", marginBottom: 12 }}
            >
                <ArrowLeft size={14} /> Tutte le conversazioni
            </Link>

            {loading ? (
                <div className="lt-card" style={{ padding: 32, textAlign: "center", color: "#8A8F88" }}>Caricamento…</div>
            ) : !data ? (
                <div className="lt-card" style={{ padding: 32, textAlign: "center", color: "#8A8F88" }}>Conversazione non disponibile.</div>
            ) : (
                <>
                    <div className="lt-card" style={{ padding: 20, marginBottom: 16 }} data-testid="admin-conv-detail-header">
                        <div className="flex items-center gap-3" style={{ marginBottom: 8 }}>
                            <Shield size={16} color="#181B20" />
                            <h1 className="lt-h1" style={{ fontSize: 24, margin: 0 }} data-testid="admin-conv-detail-title">{data.title}</h1>
                        </div>
                        <div style={{ fontSize: 13, color: "#5F6560", display: "flex", gap: 16, flexWrap: "wrap" }}>
                            <div><span className="lt-label" style={{ fontSize: 11 }}>Tipo</span><br />{data.type}</div>
                            <div><span className="lt-label" style={{ fontSize: 11 }}>Organizzazione</span><br />{data.org_name}</div>
                            {data.status && <div><span className="lt-label" style={{ fontSize: 11 }}>Stato</span><br />{data.status}</div>}
                            <div><span className="lt-label" style={{ fontSize: 11 }}>Ultimo messaggio</span><br />{fmtDT(data.last_message_at)}</div>
                        </div>
                        {(data.participants || []).length > 0 && (
                            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #EEEFEA" }}>
                                <span className="lt-label" style={{ fontSize: 11 }}>Partecipanti</span>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                                    {data.participants.map((p, idx) => (
                                        <span key={p.user_id || idx} className="lt-pill" data-testid={`admin-conv-participant-${idx}`}>
                                            {p.name} · {p.user_type === "internal" ? "staff" : "cliente"}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="lt-card" style={{ padding: 20 }} data-testid="admin-conv-detail-messages">
                        {(data.messages || []).length === 0 ? (
                            <div style={{ padding: 24, textAlign: "center", color: "#8A8F88" }}>Nessun messaggio.</div>
                        ) : data.messages.map((m) => {
                            const isInternal = m.author?.user_type === "internal";
                            return (
                                <div
                                    key={m.id}
                                    className={"lt-chatmsg " + (isInternal ? "lt-chatmsg--internal" : "lt-chatmsg--client")}
                                    data-testid={`admin-conv-msg-${m.id}`}
                                >
                                    <div className="lt-chatmsg__head">
                                        <span className="lt-chatmsg__author">{m.author?.name || "—"}</span>
                                        <span className="lt-chatmsg__time">{fmtDT(m.created_at)}</span>
                                    </div>
                                    <div className="lt-chatmsg__body">{m.text}</div>
                                </div>
                            );
                        })}
                        <div style={{ marginTop: 20, padding: 12, background: "#F4F5F2", borderRadius: 4, fontSize: 12, color: "#5F6560", textAlign: "center", fontStyle: "italic" }}>
                            Sola lettura. La risposta non è disponibile in modalità super admin.
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
